import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyDocuments.css';

interface Document {
    id: string;
    name: string;
    filename: string;
    chunk_count: number;
    upload_date: string;
    uploaded_by: string;
    tags: string[];
}

interface AdminDocumentsProps {
    apiUrl: string;
    userToken: string | null;
    isOpen: boolean;
    onClose: () => void;
}


const AdminDocuments: React.FC<AdminDocumentsProps> = ({ apiUrl, userToken, isOpen, onClose }) => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (isOpen && userToken) {
            fetchDocuments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, userToken]);

    const fetchDocuments = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${apiUrl}/admin/all-documents`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`
                }
            });
            setDocuments(response.data.documents || []);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (docId: string) => {
        try {
            await axios.delete(`${apiUrl}/admin/delete-document/${docId}`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`
                }
            });
            setDocuments(docs => docs.filter(d => d.id !== docId));
            setDeleteConfirm(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to delete document');
        }
    };

    const filteredDocs = documents.filter(doc =>
        doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.uploaded_by?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <>
            <div className="docs-panel-overlay" onClick={onClose} />
            <div className={`docs-panel admin-panel ${isOpen ? 'open' : ''}`}>
                <div className="docs-header">
                    <h3>🔐 Admin: All Documents</h3>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="admin-search">
                    <input
                        type="text"
                        placeholder="🔍 Search documents or users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading && <div className="docs-loading">Loading...</div>}

                {error && <div className="docs-error">{error}</div>}

                {!loading && filteredDocs.length === 0 && (
                    <div className="docs-empty">
                        <p>No documents found</p>
                    </div>
                )}

                <div className="docs-list">
                    {filteredDocs.map(doc => (
                        <div key={doc.id} className="doc-card admin-card">
                            <div className="doc-info">
                                <div className="doc-name">{doc.name || doc.filename}</div>
                                <div className="doc-meta">
                                    👤 {doc.uploaded_by}
                                </div>
                                <div className="doc-meta">
                                    {doc.chunk_count} chunks • {new Date(doc.upload_date).toLocaleDateString()}
                                </div>
                            </div>
                            <button
                                className="doc-delete-btn admin-delete"
                                onClick={() => setDeleteConfirm(doc.id)}
                                title="Admin delete"
                            >
                                🗑️
                            </button>
                        </div>
                    ))}
                </div>

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="confirm-modal">
                        <div className="confirm-content">
                            <p><strong>Admin Delete</strong></p>
                            <p>Permanently remove this document?</p>
                            <div className="confirm-actions">
                                <button
                                    className="confirm-yes"
                                    onClick={() => handleDelete(deleteConfirm)}
                                >
                                    Delete
                                </button>
                                <button
                                    className="confirm-no"
                                    onClick={() => setDeleteConfirm(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AdminDocuments;
